import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  User,
  Phone,
  Package,
  Truck,
  Clock,
  DollarSign,
  Calendar,
  Navigation,
} from 'lucide-react';
import socketService from '@/services/socket';
import { getOrderByIdForAdmin } from '@/api/orderService';
// Fix Leaflet icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { getStatusColor, getStatusIcon, getStatusLabel } from '../../utils/statusUtils';
import GlobalLoader from "../../components/ui/GlobalLoader";
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom driver icon (moving truck)
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  className: 'driver-marker-icon',
});

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  pickupStatus: string;
  totalPrice: number;
  deliveryCharges: number;
  deliveryDate: string;
  deliveryStatus: string;
  createdAt: string;
  medicines: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  assignedDriver?: {
    id: string;
    name: string;
    phone: string;
  };
  location?: any |  {
    address?: string;
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
  deliveryLocation?: {
    address?: string;
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
  customerLocation?: {
    address?: string;
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
  coordinates?: {
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
}

interface DriverLocation {
  latitude: number;
  longitude: number;
  speed?: number;
  heading?: number;
  timestamp: string;
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(null);

  // Default map center (will be updated based on order location)
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([40.0583, -74.4057]); // Default: New Jersey

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  useEffect(() => {
    if (!order?.assignedDriver?.id) return;

    // Subscribe to driver location updates
    const handleLocationUpdate = (data: any) => {
      if (data.driverId === order.assignedDriver?.id) {
        console.log('Driver location update:', data);
        setDriverLocation({
          latitude: data.latitude,
          longitude: data.longitude,
          speed: data.speed,
          heading: data.heading,
          timestamp: data.timestamp,
        });
      }
    };

    // Subscribe to delivery pickupStatus updates
    const handleDeliveryStatus = (data: any) => {
      if (data.orderId === orderId) {
        console.log('Delivery pickupStatus update:', data);
        // Silent update - refresh order details without toast
        fetchOrderDetails();
      }
    };

    socketService.onLocationUpdate(handleLocationUpdate);
    socketService.onDeliveryStatusUpdate(handleDeliveryStatus);
    socketService.subscribeToDriver(order.assignedDriver.id);
    socketService.subscribeToOrder(orderId!);

    // Cleanup
    return () => {
      socketService.offLocationUpdate(handleLocationUpdate);
      socketService.offDeliveryStatusUpdate(handleDeliveryStatus);
      socketService.unsubscribeFromDriver(order.assignedDriver!.id);
      socketService.unsubscribeFromOrder(orderId!);
    };
  }, [order?.assignedDriver?.id, orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      
      if (!orderId) {
        console.error('No orderId provided');
        toast.error('Invalid order ID');
        return;
      }

      const response = await getOrderByIdForAdmin(orderId);
      
      if (response.success && response.data) {
        console.log('Order data received:', response.data);
        setOrder(response.data);
        
        // Try multiple possible location field structures
        const locationData = 
          response.data.location || 
          response.data.deliveryLocation || 
          response.data.customerLocation ||
          response.data.coordinates;
        
        if (locationData && locationData.lat && locationData.lng) {
          setMapCenter([locationData.lat, locationData.lng]);
          console.log('Map center set to:', [locationData.lat, locationData.lng]);
        } else if (locationData && locationData.latitude && locationData.longitude) {
          setMapCenter([locationData.latitude, locationData.longitude]);
          console.log('Map center set to:', [locationData.latitude, locationData.longitude]);
        } else {
          console.warn('No valid location data found in order, using default map center');
        }
      } else {
        toast.error('Failed to load order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (pickupStatus: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      'in-transit': 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[pickupStatus] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-600">Order not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Order Tracking</h1>
            <p className="text-gray-500 mt-1">Order #{order.orderNumber}</p>
          </div>
        </div>
        <Badge className={getStatusColor(order.deliveryStatus)}>
          {getStatusIcon(order.deliveryStatus)}
          <span className="ml-1">{order.deliveryStatus?.replace('-', ' ')}</span>
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Customer Info */}
          <Card className="shadow-md">
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Name</p>
                <p className="font-semibold text-gray-900">{order.customerName}</p>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">Phone</p>
                  <p className="font-semibold text-gray-900">{order.customerPhone}</p>
                </div>
              </div>
              {/* <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-500 mt-1" />
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Delivery Address</p>
                  <p className="font-medium text-gray-900">{order.customerAddress}</p>
                </div>
              </div> */}
              <div>
                <p className="font-semibold my-2">
                  Delivery Address:
                </p>
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4 mr-2" />
                  {
                    order.location === 'string' 
                    ? order.location 
                    : order.location?.address || ''
                  }
                </div>                            

              </div>
            </CardContent>
          </Card>

          {/* Driver Info */}
          {order.assignedDriver && (
            <Card className="shadow-md">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-green-600" />
                  Assigned Driver
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Name</p>
                  <p className="font-semibold text-gray-900">{order.assignedDriver.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Phone</p>
                    <p className="font-semibold text-gray-900">{order.assignedDriver.phone}</p>
                  </div>
                </div>
                {driverLocation && (
                  <>
                    <div className="pt-3 border-t">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                        <span className="text-sm font-semibold text-green-600">Live Tracking Active</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Speed</p>
                          <p className="font-medium text-gray-900">{driverLocation.speed || 0} mph</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">Last Update</p>
                          <p className="font-medium text-gray-900">
                            {new Date(driverLocation.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Order Items */}
          {order.medicines.length > 0 && (
            <Card className="shadow-md">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Order Items ({order.medicines.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-6">
                <div className="space-y-3">
                  {order.medicines.map((medicine, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{medicine.name}</p>
                        <p className="text-sm text-gray-600">Quantity: {medicine.quantity}</p>
                      </div>
                      <p className="font-semibold text-gray-900">${(medicine.price || 0).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Order Metadata */}
          <Card className="shadow-md">
            <CardHeader className="bg-gray-50">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(order.deliveryStatus)}>
                  {getStatusIcon(order.deliveryStatus)}
                  <span className="ml-1">{order.deliveryStatus?.replace('-', ' ')}</span>
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">Created</p>
                  <p className="font-medium text-gray-900">
                    {new Date(order.createdAt).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              {order.deliveryDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Delivery Date</p>
                    <p className="font-medium text-gray-900">{order.deliveryDate}</p>
                  </div>
                </div>
              )}
              {(order as any).deliveryTime && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Delivery Time</p>
                    <p className="font-medium text-gray-900">{(order as any).deliveryTime}</p>
                  </div>
                </div>
              )}
              {order.deliveryCharges > 0 && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Delivery Charges</p>
                    <p className="font-medium text-gray-900">${order.deliveryCharges.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="h-[900px] shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="flex items-center justify-between">
                {/* <div className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-blue-600" />
                  <span>Live Delivery Tracking</span>
                </div> */}
                {driverLocation && (
                  <Badge variant="outline" className="bg-white">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                    Live
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 h-[calc(100%-80px)]">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                className="rounded-b-lg"
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Delivery destination marker */}
                {(() => {
                  const loc = order.location || order.deliveryLocation || order.customerLocation || order.coordinates;
                  const lat = loc?.lat || loc?.latitude;
                  const lng = loc?.lng || loc?.longitude;
                  
                  return lat && lng ? (
                    <Marker position={[lat, lng]}>
                      <Popup>
                        <div className="p-2">
                          <p className="font-semibold text-gray-900">Delivery Destination</p>
                          <p className="text-sm text-gray-600 mt-1">{order.customerAddress}</p>
                          <p className="text-xs text-gray-500 mt-1">Customer: {order.customerName}</p>
                        </div>
                      </Popup>
                    </Marker>
                  ) : null;
                })()}

                {/* Driver location marker */}
                {driverLocation && (
                  <Marker
                    position={[driverLocation.latitude, driverLocation.longitude]}
                    icon={driverIcon}
                  >
                    <Popup>
                      <div className="p-2">
                        <p className="font-semibold text-gray-900">Driver Location</p>
                        <p className="text-sm text-gray-600 mt-1">{order.assignedDriver?.name}</p>
                        <p className="text-sm text-gray-600">Speed: {driverLocation.speed || 0} mph</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Updated: {new Date(driverLocation.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Route line */}
                {(() => {
                  const loc = order.location || order.deliveryLocation || order.customerLocation || order.coordinates;
                  const lat = loc?.lat || loc?.latitude;
                  const lng = loc?.lng || loc?.longitude;
                  
                  return lat && lng && driverLocation ? (
                    <Polyline
                      positions={[
                        [driverLocation.latitude, driverLocation.longitude],
                        [lat, lng],
                      ]}
                      color="blue"
                      weight={3}
                      dashArray="10, 10"
                    />
                  ) : null;
                })()}
              </MapContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

